import { useDialog } from "../components/DialogProvider";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { hasGroup, useAuth } from "../auth/AuthContext";
import { ApiError } from "../api/client";
import {
  archiveProfile,
  createProfile,
  fetchPrompts,
  updateCommon,
  updateProfile,
  type AdminPromptsResponse,
  type ProfileInput,
  type PromptProfile,
} from "../api/prompts";
import { invalidateSummaryProfiles } from "../summaryProfiles";

/** Должно совпадать с recorder-backend AUTHENTIK_ADMIN_GROUP. */
const ADMIN_GROUP = "recordings-admin";

type Tab = "profiles" | "common" | "fallback";

const emptyForm: ProfileInput = {
  slug: "",
  label: "",
  hint: "",
  intro: "",
  sections: "",
  common_slug: "default",
  sort_order: 0,
};

export default function PromptsPage() {
  const { user } = useAuth();
  const isAdmin = hasGroup(user, ADMIN_GROUP);

  const [data, setData] = useState<AdminPromptsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("profiles");

  const reload = useCallback(async () => {
    // Любой заход/правка в редакторе бустит общий кэш пикеров и лейблов
    // (summaryProfiles.ts), чтобы «Другой тип»/табы/загрузка не показывали
    // старое название до перезагрузки страницы.
    invalidateSummaryProfiles();
    setLoading(true);
    setLoadError(null);
    try {
      setData(await fetchPrompts());
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) void reload();
  }, [isAdmin, reload]);

  if (!isAdmin) {
    return (
      <div className="page">
        <h1>Промпты</h1>
        <p className="muted">Раздел доступен только администраторам.</p>
        <Link to="/">← К записям</Link>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="summary-head">
        <h1>Промпты саммари</h1>
        <Link to="/" className="link-button">
          ← К записям
        </Link>
      </div>

      <div className="summary-tabs">
        <TabButton active={tab === "profiles"} onClick={() => setTab("profiles")}>
          Профили
        </TabButton>
        <TabButton active={tab === "common"} onClick={() => setTab("common")}>
          Общие правила
        </TabButton>
        <TabButton active={tab === "fallback"} onClick={() => setTab("fallback")}>
          Аварийный
        </TabButton>
      </div>

      {loading && <p className="muted">Загрузка…</p>}
      {loadError && <p className="error">Не удалось загрузить: {loadError}</p>}

      {data && tab === "profiles" && (
        <ProfilesTab data={data} onChanged={reload} />
      )}
      {data && tab === "common" && <CommonTab data={data} onChanged={reload} />}
      {data && tab === "fallback" && <FallbackTab />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <span className={"summary-tab" + (active ? " summary-tab--active" : "")}>
      <button type="button" className="summary-tab-label" onClick={onClick}>
        {children}
      </button>
    </span>
  );
}

// ─── Профили ─────────────────────────────────────────────────────────────────

function ProfilesTab({
  data,
  onChanged,
}: {
  data: AdminPromptsResponse;
  onChanged: () => Promise<void>;
}) {
  const { confirm } = useDialog();
  // null = режим создания нового профиля; иначе slug редактируемого.
  const [selected, setSelected] = useState<string | null>(
    data.profiles[0]?.slug ?? null,
  );
  const creating = selected === null;

  const current = useMemo(
    () => data.profiles.find((p) => p.slug === selected) ?? null,
    [data.profiles, selected],
  );

  const [form, setForm] = useState<ProfileInput>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Подхватываем выбранный профиль в форму.
  useEffect(() => {
    setMsg(null);
    if (current) {
      setForm({
        slug: current.slug,
        label: current.label,
        hint: current.hint,
        intro: current.intro,
        sections: current.sections,
        common_slug: current.common_slug,
        sort_order: current.sort_order,
      });
    } else {
      setForm(emptyForm);
    }
  }, [current, creating]);

  const commonBody = useMemo(
    () => data.commons.find((c) => c.slug === form.common_slug)?.body ?? "",
    [data.commons, form.common_slug],
  );

  const set = <K extends keyof ProfileInput>(k: K, v: ProfileInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      if (creating) {
        await createProfile(form);
      } else {
        await updateProfile(form.slug || "", form);
      }
      await onChanged();
      if (creating) setSelected(form.slug || null);
      setMsg({ ok: true, text: "Сохранено." });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  };

  const onArchive = async (p: PromptProfile) => {
    if (!await confirm({ title: "Архивировать профиль?", description: `Профиль «${p.label}» исчезнет из списков выбора.`, confirmLabel: "Архивировать" })) {
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await archiveProfile(p.slug);
      await onChanged();
      setSelected(null);
      setMsg({ ok: true, text: "Профиль архивирован." });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="prompts-layout">
      {/* Левая колонка — список профилей */}
      <div className="prompts-sidebar">
        <ul className="prompts-list">
          {data.profiles.map((p) => (
            <li key={p.slug}>
              <button
                type="button"
                className={
                  "prompts-list-item" +
                  (p.slug === selected ? " prompts-list-item--active" : "") +
                  (p.archived ? " prompts-list-item--archived" : "")
                }
                onClick={() => setSelected(p.slug)}
              >
                {p.label}
                {p.archived ? " (архив)" : ""}
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="page-button prompts-new-btn"
          onClick={() => setSelected(null)}
        >
          ＋ Новый профиль
        </button>
      </div>

      {/* Правая колонка — редактор */}
      <div className="prompts-editor">
        <p className="prompts-editor-hint">
          Итоговый промпт: <b>роль и цель</b> → общие правила → формат →{" "}
          <b>секции</b> → транскрипт. Жирным — то, что вы редактируете.
        </p>

        {creating && (
          <Field label="Код (slug) — латиница, цифры, _; менять потом нельзя">
            <input
              className="filter-input"
              value={form.slug}
              onChange={(e) => set("slug", e.target.value)}
              placeholder="например, standup"
            />
          </Field>
        )}

        <Field label="Название (лейбл в пикерах)">
          <input
            className="filter-input"
            value={form.label}
            onChange={(e) => set("label", e.target.value)}
          />
        </Field>

        <Field label="Подсказка под выбором (hint)">
          <input
            className="filter-input"
            value={form.hint}
            onChange={(e) => set("hint", e.target.value)}
          />
        </Field>

        <Field label="1 · Роль и цель (редактируется)">
          <textarea
            className="filter-input prompts-textarea"
            value={form.intro}
            onChange={(e) => set("intro", e.target.value)}
          />
        </Field>

        <Field label="Общие правила (read-only; меняются во вкладке «Общие правила»)">
          <select
            className="filter-input"
            value={form.common_slug}
            onChange={(e) => set("common_slug", e.target.value)}
          >
            {data.commons.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.label} ({c.slug})
              </option>
            ))}
          </select>
          <pre className="prompts-readonly">{commonBody}</pre>
        </Field>

        <Field label="Формат ответа (закрыт — от него зависит парсер)">
          <pre className="prompts-readonly">{data.format_header}</pre>
        </Field>

        <Field label="4 · Секции профиля (редактируется)">
          <textarea
            className="filter-input prompts-textarea"
            value={form.sections}
            onChange={(e) => set("sections", e.target.value)}
          />
        </Field>

        <p className="prompts-note">
          Изменения применяются к НОВЫМ саммари. Для существующих записей нажмите
          «Другой тип → Перегенерировать».
        </p>

        {msg && <p className={msg.ok ? "prompts-msg-ok" : "error"}>{msg.text}</p>}

        <div className="modal-actions">
          <button
            type="button"
            className="page-button primary"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Сохранение…" : creating ? "Создать" : "Сохранить"}
          </button>
          {current && !current.archived && (
            <button
              type="button"
              className="page-button"
              onClick={() => onArchive(current)}
              disabled={saving}
            >
              Архивировать
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Общие правила ───────────────────────────────────────────────────────────

function CommonTab({
  data,
  onChanged,
}: {
  data: AdminPromptsResponse;
  onChanged: () => Promise<void>;
}) {
  const [slug, setSlug] = useState<string>(data.commons[0]?.slug ?? "default");
  const current = data.commons.find((c) => c.slug === slug) ?? null;
  const [label, setLabel] = useState(current?.label ?? "");
  const [body, setBody] = useState(current?.body ?? "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    setLabel(current?.label ?? "");
    setBody(current?.body ?? "");
    setMsg(null);
  }, [current]);

  const onSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await updateCommon(slug, { label, body });
      await onChanged();
      setMsg({ ok: true, text: "Сохранено." });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="prompts-narrow">
      <p className="prompts-editor-hint">
        Общая часть приклеивается ко всем профилям, которые её выбрали. Меняете
        здесь — меняется во всех таких профилях (для новых саммари).
      </p>
      {data.commons.length > 1 && (
        <Field label="Запись общих правил">
          <select
            className="filter-input"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
          >
            {data.commons.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.label} ({c.slug})
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Название">
        <input
          className="filter-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>
      <Field label="Текст общих правил">
        <textarea
          className="filter-input prompts-textarea prompts-textarea--tall"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      {msg && <p className={msg.ok ? "prompts-msg-ok" : "error"}>{msg.text}</p>}
      <div className="modal-actions">
        <button
          type="button"
          className="page-button primary"
          onClick={onSave}
          disabled={saving}
        >
          {saving ? "Сохранение…" : "Сохранить"}
        </button>
      </div>
    </div>
  );
}

// ─── Аварийный ───────────────────────────────────────────────────────────────

function FallbackTab() {
  return (
    <div className="prompts-narrow">
      <p>
        Аварийный промпт захардкожен в воркере транскрибации и НЕ редактируется
        — он срабатывает, только если инструкция профиля не доехала до воркера
        (старый бэкенд, удалённый профиль или сбой БД). Он нарочито простой:
        короткий конспект «Кратко» + «Главные пункты».
      </p>
      <p className="prompts-note">
        Если сработал фолбэк, саммари начинается строкой{" "}
        <code>⚠️ Базовый конспект — профиль «…» не загрузился.</code> — это
        сигнал, что каталог промптов недоступен, и стоит проверить бэкенд/воркер.
      </p>
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="prompts-field">
      <label className="manager-label">{label}</label>
      {children}
    </div>
  );
}
